import { TooltipButton } from './common/TooltipButton';

import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ExpirationDatePickerProps {
  currentValue: Date | string | null;
  onSave: (date: Date) => void;
  onClose: () => void;
  minDate?: Date;
}

export const ExpirationDatePicker: React.FC<ExpirationDatePickerProps> = ({ currentValue, onSave, onClose, minDate }) => {
  const { t, i18n } = useTranslation();
  
  const initialDate = useMemo(() => {
    if (!currentValue) return new Date();
    let dObj: Date;
    if (typeof currentValue === 'string') {
      const parts = currentValue.split('-').map(Number);
      if (parts.length === 3 && !parts.some(isNaN)) {
        dObj = new Date(parts[0], parts[1] - 1, parts[2]);
      } else {
        dObj = new Date(currentValue);
      }
    } else {
      dObj = new Date(currentValue);
    }
    return isNaN(dObj.getTime()) ? new Date() : dObj;
  }, [currentValue]);

  const [viewDate, setViewDate] = useState(new Date(initialDate));
  const [selectedDate, setSelectedDate] = useState(new Date(initialDate));
  const [mode, setMode] = useState<'calendar' | 'month-year'>('calendar');
  const [activeDropdown, setActiveDropdown] = useState<'year' | 'month' | null>(null);
  
  // Ensure we sync state if initialDate changes significantly (e.g. from invalid to valid)
  useEffect(() => {
    setViewDate(new Date(initialDate));
    setSelectedDate(new Date(initialDate));
  }, [initialDate]);

  const daysInMonth = useMemo(() => {
    const y = viewDate.getFullYear();
    const m = viewDate.getMonth();
    if (isNaN(y) || isNaN(m)) return { firstDay: 0, days: 30 };
    const firstDay = new Date(y, m, 1).getDay();
    const days = new Date(y, m + 1, 0).getDate();
    return { firstDay, days };
  }, [viewDate]);

  const monthName = useMemo(() => {
    try {
      return viewDate.toLocaleString(i18n.language, { month: 'long' });
    } catch (e) {
      return '';
    }
  }, [viewDate, i18n.language]);

  const year = viewDate.getFullYear();

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const list = [];
    for (let y = currentYear; y <= currentYear + 10; y++) {
      list.push(y);
    }
    return list;
  }, []);

  const months = useMemo(() => {
    const list = [];
    for (let m = 0; m < 12; m++) {
      list.push({
        index: m,
        name: new Date(2000, m, 1).toLocaleString(i18n.language, { month: 'long' })
      });
    }
    return list;
  }, [i18n.language]);

  const handleDaySelect = (day: number) => {
    const targetDate = new Date(year, viewDate.getMonth(), day);
    if (minDate) {
      const targetD = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
      const minD = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
      if (targetD < minD) return;
    }
    setSelectedDate(targetDate);
  };

  const handleMonthYearSelect = (newMonth: number, newYear: number) => {
    const next = new Date(newYear, newMonth, 1);
    setViewDate(next);
    setMode('calendar');
  };

  const changeMonth = (e: React.MouseEvent, offset: number) => {
    e.preventDefault();
    const next = new Date(viewDate.getFullYear(), viewDate.getMonth() + offset, 1);
    setViewDate(next);
  };

  const handleSave = (e: React.MouseEvent) => {
    e.preventDefault();
    onSave(selectedDate);
  };

  const isSelected = (day: number) => {
    return selectedDate.getDate() === day && 
           selectedDate.getMonth() === viewDate.getMonth() && 
           selectedDate.getFullYear() === viewDate.getFullYear();
  };

  const isToday = (day: number) => {
    const now = new Date();
    return now.getDate() === day && 
           now.getMonth() === viewDate.getMonth() && 
           now.getFullYear() === viewDate.getFullYear();
  };

  const isPast = (day: number) => {
    if (!minDate) return false;
    const target = new Date(year, viewDate.getMonth(), day);
    // Normalize to date only for comparison
    const targetD = new Date(target.getFullYear(), target.getMonth(), target.getDate());
    const minD = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
    return targetD < minD;
  };

  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => { e.preventDefault(); onClose(); }}
        className="fixed inset-0 bg-[#1a2b3c]/60 backdrop-blur-md"
      />
      <motion.div 
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="relative bg-white w-full max-w-sm rounded-[3rem] shadow-2xl overflow-hidden flex flex-col"
      >
        <div className="bg-[#1a2b3c] p-6 text-white">
          <div className="flex justify-between items-center mb-4">
             <div className="flex items-center gap-2">
                <div className="p-2 bg-emerald-500 rounded-xl text-white">
                  <CalendarIcon size={16} />
                </div>
                <h3 className="font-black text-xs uppercase tracking-widest">{t('product.expiration_label')}</h3>
             </div>
             <TooltipButton label={t('common.close', { defaultValue: 'Close' })} type="button" onClick={(e) => { e.preventDefault(); onClose(); }} className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors">
                <X size={18} />
             </TooltipButton>
          </div>
          <div className="flex flex-col gap-1">
            <div className="text-3xl font-black tracking-tighter">
              {selectedDate.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex justify-between items-center px-2">
             <TooltipButton label={t('common.previous', { defaultValue: 'Previous' })} type="button" onClick={(e) => changeMonth(e, -1)} className="p-2 text-gray-400 hover:text-[#1a2b3c]"><ChevronLeft size={20} /></TooltipButton>
             <button 
                type="button"
                onClick={(e) => { e.preventDefault(); setMode(mode === 'calendar' ? 'month-year' : 'calendar'); }}
                className="font-black text-sm uppercase tracking-widest text-[#1a2b3c] hover:text-emerald-500 transition-colors px-4 py-1 rounded-full hover:bg-emerald-50"
             >
                {monthName} {year}
             </button>
             <TooltipButton label={t('common.next', { defaultValue: 'Next' })} type="button" onClick={(e) => changeMonth(e, 1)} className="p-2 text-gray-400 hover:text-[#1a2b3c]"><ChevronRight size={20} /></TooltipButton>
          </div>

          {mode === 'calendar' ? (
            <div className="grid grid-cols-7 gap-1">
              {(t('datetime.weekdays_short', { returnObjects: true }) as string[]).map((d, index) => (
                <div key={`${d}-${index}`} className="h-8 flex items-center justify-center text-[10px] font-black text-gray-300 uppercase">{d}</div>
              ))}
              {Array.from({ length: daysInMonth.firstDay }).map((_, i) => <div key={`empty-${i}`} />)}
              {Array.from({ length: daysInMonth.days }).map((_, i) => {
                const day = i + 1;
                const past = isPast(day);
                const selected = isSelected(day);
                const today = isToday(day);
                return (
                  <button
                    key={day}
                    type="button"
                    disabled={past}
                    onClick={(e) => { e.preventDefault(); handleDaySelect(day); }}
                    className={`
                      h-10 w-10 rounded-2xl text-xs font-black transition-all flex items-center justify-center relative
                      ${past ? 'text-gray-100' : 'text-[#1a2b3c]'}
                      ${selected 
                        ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-100 scale-110' 
                        : !past ? 'hover:bg-gray-50 active:bg-emerald-100' : ''}
                      ${today && !selected ? 'text-emerald-500' : ''}
                    `}
                  >
                    {day}
                    {today && !selected && <div className="absolute bottom-1 w-1 h-1 bg-emerald-500 rounded-full" />}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Year Dropdown */}
              <div className="space-y-2 relative">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">{t('datetime.year', 'Year')}</label>
                <button 
                  type="button"
                  onClick={(e) => { e.preventDefault(); setActiveDropdown(activeDropdown === 'year' ? null : 'year'); }}
                  className="w-full bg-gray-50 rounded-2xl px-6 py-4 font-bold text-[#1a2b3c] flex items-center justify-between border-2 border-transparent hover:border-emerald-500/20 transition-all outline-none"
                >
                  <span className="text-sm">{viewDate.getFullYear()}</span>
                  <ChevronDown size={18} className={`text-gray-400 transition-transform duration-300 ${activeDropdown === 'year' ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {activeDropdown === 'year' && (
                    <>
                      <div className="fixed inset-0 z-[10]" onClick={(e) => { e.preventDefault(); setActiveDropdown(null); }} />
                      <motion.div 
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        className="absolute z-[20] left-0 right-0 top-full mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 max-h-48 overflow-y-auto scrollbar-hide"
                      >
                        {years.map(y => (
                          <button
                            key={y}
                            type="button"
                            onClick={(e) => { e.preventDefault(); handleMonthYearSelect(viewDate.getMonth(), y); setActiveDropdown(null); }}
                            className={`w-full text-left px-6 py-3 text-xs font-black uppercase tracking-widest transition-colors ${viewDate.getFullYear() === y ? 'bg-emerald-50 text-emerald-600' : 'text-[#1a2b3c] hover:bg-gray-50'}`}
                          >
                            {y}
                          </button>
                        ))}
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>

              {/* Month Dropdown */}
              <div className="space-y-2 relative">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">{t('datetime.month', 'Month')}</label>
                <button 
                  type="button"
                  onClick={(e) => { e.preventDefault(); setActiveDropdown(activeDropdown === 'month' ? null : 'month'); }}
                  className="w-full bg-gray-50 rounded-2xl px-6 py-4 font-bold text-[#1a2b3c] flex items-center justify-between border-2 border-transparent hover:border-emerald-500/20 transition-all outline-none"
                >
                  <span className="text-sm">{months[viewDate.getMonth()]?.name || ''}</span>
                  <ChevronDown size={18} className={`text-gray-400 transition-transform duration-300 ${activeDropdown === 'month' ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {activeDropdown === 'month' && (
                    <>
                      <div className="fixed inset-0 z-[10]" onClick={(e) => { e.preventDefault(); setActiveDropdown(null); }} />
                      <motion.div 
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        className="absolute z-[20] left-0 right-0 top-full mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 max-h-48 overflow-y-auto scrollbar-hide"
                      >
                        {months.map((m) => (
                          <button
                            key={m.index}
                            type="button"
                            onClick={(e) => { e.preventDefault(); handleMonthYearSelect(m.index, viewDate.getFullYear()); setActiveDropdown(null); }}
                            className={`w-full text-left px-6 py-3 text-xs font-black uppercase tracking-widest transition-colors ${viewDate.getMonth() === m.index ? 'bg-emerald-50 text-emerald-600' : 'text-[#1a2b3c] hover:bg-gray-50'}`}
                          >
                            {m.name}
                          </button>
                        ))}
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>

              <button 
                type="button"
                onClick={(e) => { e.preventDefault(); setMode('calendar'); }}
                className="w-full py-4 text-emerald-500 font-black text-[10px] uppercase tracking-[0.2em] bg-emerald-50 rounded-2xl active:scale-95 transition-all mt-2"
              >
                {t('datetime.back_to_calendar', 'Back to Calendar')}
              </button>
            </div>
          )}

          <button 
            type="button"
            onClick={handleSave}
            className="w-full bg-[#1a2b3c] text-white py-5 rounded-[2rem] font-black text-sm uppercase tracking-widest flex items-center justify-center gap-2 shadow-xl active:scale-95 transition-all mt-4"
          >
            <Check size={18} /> {t('common.save')}
          </button>
        </div>
      </motion.div>
    </div>
  );
};
